import React from "react";
import { FiCheckCircle, FiClock, FiTruck, FiPackage, FiRefreshCw } from "react-icons/fi";
import { motion } from "framer-motion";

const deliverySteps = [
  { key: "PROCESSING", label: "Ordered", icon: FiClock },
  { key: "SHIPPED", label: "Shipped", icon: FiPackage },
  { key: "ON_THE_WAY", label: "En Route", icon: FiTruck },
  { key: "BOOK_WITH_ME", label: "Delivered", icon: FiCheckCircle },
];

const returnSteps = [
  { key: "RETURN_REQUESTED", label: "Requested", icon: FiRefreshCw },
  { key: "PICKUP_SCHEDULED", label: "Scheduled", icon: FiClock },
  { key: "RETURN_IN_TRANSIT", label: "Returning", icon: FiTruck },
  { key: "RETURNED", label: "Returned", icon: FiCheckCircle },
];

const OrderTimeline = ({ currentStatus }) => {
  const isReturnFlow = currentStatus.startsWith("RETURN") || currentStatus === "PICKUP_SCHEDULED";
  const steps = isReturnFlow ? returnSteps : deliverySteps;
  const currentIndex = steps.findIndex(step => step.key === currentStatus);

  return (
    <div className="relative py-4 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        <motion.div 
          className="flex justify-between"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
        >
          {steps.map((step, index) => {
            const isCompleted = index <= currentIndex;
            const isActive = index === currentIndex;
            const Icon = step.icon;

            return (
              <div 
                key={step.key}
                className="relative flex flex-col items-center w-1/4"
              >
                {/* Progress line */}
                {index > 0 && (
                  <div className="absolute top-5 left-[-50%] w-full h-1 bg-gray-200 dark:bg-slate-700 rounded-full">
                    <motion.div
                      className="absolute h-full bg-gradient-to-r from-primary-shade3 to-primary-shade4 rounded-full"
                      initial={{ width: 0 }}
                      animate={{ width: isCompleted ? "100%" : "0%" }}
                      transition={{ duration: 0.8, delay: 0.2 }}
                    />
                  </div>
                )}

                {/* Step indicator */}
                <motion.div
                  className={`relative z-10 flex items-center justify-center w-10 h-10 rounded-full shadow-lg ${
                    isCompleted 
                      ? "bg-gradient-to-br from-primary-shade3 to-primary-shade4"
                      : "bg-white dark:bg-slate-800"
                  }`}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  {isCompleted ? (
                    <FiCheckCircle className="w-5 h-5 text-white" />
                  ) : (
                    <Icon className={`w-5 h-5 ${
                      isActive 
                        ? "text-primary-shade4 animate-pulse"
                        : "text-gray-400 dark:text-slate-500"
                    }`} />
                  )}
                </motion.div>

                {/* Active ring */}
                {isActive && (
                  <motion.div
                    className="absolute inset-0 border-2 border-primary-shade3 rounded-full"
                    initial={{ scale: 0.8, opacity: 0 }}
                    animate={{ scale: 1.2, opacity: 0.4 }}
                    transition={{ 
                      repeat: Infinity,
                      duration: 1.5,
                      ease: "easeInOut"
                    }}
                  />
                )}

                {/* Label */}
                <span className={`mt-4 text-sm font-medium text-center ${
                  isCompleted || isActive
                    ? "text-primary-shade4 dark:text-primary-shade2"
                    : "text-gray-400 dark:text-slate-500"
                }`}>
                  {step.label}
                </span>

                {/* Status dot */}
                {isActive && (
                  <motion.div
                    className="absolute top-12 w-2 h-2 bg-primary-shade4 rounded-full"
                    initial={{ y: -10, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ 
                      type: "spring",
                      stiffness: 200,
                      damping: 10
                    }}
                  />
                )}
              </div>
            );
          })}
        </motion.div>
      </div>
    </div>
  );
};

export default OrderTimeline;